import { z } from 'zod'

import type { LookupOption } from '@/ingest-client'

/** Data Vault › Marketplace API Data: view state and the input parsers, beside the page. */

export const OPTIONS = [
  'listing',
  'pricing',
  'fee_estimate',
] as const satisfies readonly LookupOption[]

/** The page's view state: which store, which option. The pasted ASINs are a draft, not view state. */
export const marketplaceDataSearchSchema = z.object({
  store: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The Amazon seller or vendor store to look up in, by id.'),
  option: z
    .enum(OPTIONS)
    .optional()
    .catch(undefined)
    .describe(
      'What to ask Amazon for: listing details, offers (pricing) or a fee estimate. Default listing.',
    ),
})
export type MarketplaceDataSearch = z.infer<typeof marketplaceDataSearchSchema>

/** The limits the server enforces, until its own list has loaded. */
export const ASIN_FALLBACK_LIMITS: Record<LookupOption, number> = {
  listing: 100,
  pricing: 200,
  fee_estimate: 100,
}

export const OPTION_ICON: Record<LookupOption, string> = {
  listing: 'inventory_2',
  pricing: 'sell',
  fee_estimate: 'calculate',
}

const ASIN = /^[A-Z0-9]{10}$/

/** Split pasted text as the server does: commas, semicolons, whitespace; upper-cased, de-duplicated. */
export function parseAsins(raw: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = []
  const invalid: string[] = []
  const seen = new Set<string>()
  for (const token of raw.split(/[\s,;]+/)) {
    const asin = token.trim().toUpperCase()
    if (!asin || seen.has(asin)) continue
    seen.add(asin)
    ;(ASIN.test(asin) ? valid : invalid).push(asin)
  }
  return { valid, invalid }
}

export type FeeLine = { asin: string; selling_price: string }

/**
 * `ASIN<tab or comma>price`, one per line — what a paste from Excel or a CSV
 * gives. A header row is skipped; each problem names its line.
 */
export function parseFeeLines(raw: string): { items: FeeLine[]; problems: number[] } {
  const items: FeeLine[] = []
  const problems: number[] = []
  raw.split(/\r?\n/).forEach((line, index) => {
    const [asinCell = '', priceCell = ''] = line.split(/[\t,;]/).map((cell) => cell.trim())
    if (!asinCell && !priceCell) return
    const asin = asinCell.toUpperCase()
    if (index === 0 && asin === 'ASIN') return
    const price = Number(priceCell)
    if (!ASIN.test(asin) || !priceCell || !Number.isFinite(price) || price <= 0) {
      problems.push(index + 1)
      return
    }
    items.push({ asin, selling_price: priceCell })
  })
  return { items, problems }
}

/** The localName-matched descendants of an XML node: xlsx parts may or may not prefix their tags. */
function tags(node: Document | Element, name: string): Element[] {
  return Array.from(node.getElementsByTagNameNS('*', name))
}

/** A cell reference's column, from 0: `A1` → 0, `AB7` → 27. */
function columnOf(ref: string): number {
  let column = 0
  for (const letter of ref.replace(/\d+$/, '').toUpperCase())
    column = column * 26 + (letter.charCodeAt(0) - 64)
  return column - 1
}

/**
 * An Excel workbook's first sheet as tab-separated lines, for the same
 * parser a paste goes through. Read with `jszip` (an .xlsx is a zip of XML),
 * loaded only when an Excel file is picked.
 */
export async function xlsxToText(data: ArrayBuffer): Promise<string> {
  const { default: JSZip } = await import('jszip')
  const zip = await JSZip.loadAsync(data)
  const xml = async (path: string) => {
    const part = zip.file(path)
    return part
      ? new DOMParser().parseFromString(await part.async('text'), 'application/xml')
      : null
  }
  // The first sheet, through the workbook's relationships; sheet1 if they are missing.
  const workbook = await xml('xl/workbook.xml')
  const rels = await xml('xl/_rels/workbook.xml.rels')
  // `r:id`, whatever the relationships namespace is prefixed as.
  const first = workbook ? tags(workbook, 'sheet')[0] : undefined
  const firstId = Array.from(first?.attributes ?? []).find((attr) => attr.localName === 'id')?.value
  const target = rels
    ? tags(rels, 'Relationship')
        .find((rel) => rel.getAttribute('Id') === firstId)
        ?.getAttribute('Target')
    : undefined
  const path = target ? (target.startsWith('/') ? target.slice(1) : `xl/${target}`) : ''
  const sheet = (path ? await xml(path) : null) ?? (await xml('xl/worksheets/sheet1.xml'))
  if (!sheet) throw new Error('The workbook has no sheet')

  const shared = await xml('xl/sharedStrings.xml')
  const strings = shared
    ? tags(shared, 'si').map((si) =>
        tags(si, 't')
          .map((t) => t.textContent ?? '')
          .join(''),
      )
    : []

  return tags(sheet, 'row')
    .map((row) => {
      const cells: string[] = []
      for (const cell of tags(row, 'c')) {
        const type = cell.getAttribute('t')
        const raw = tags(cell, 'v')[0]?.textContent ?? ''
        const value =
          type === 's'
            ? (strings[Number(raw)] ?? '')
            : type === 'inlineStr'
              ? tags(cell, 't')
                  .map((t) => t.textContent ?? '')
                  .join('')
              : // A number as Excel stores it (49.990000000000002) reads as the one typed.
                type === null || type === 'n'
                ? raw === ''
                  ? ''
                  : String(Number(raw))
                : raw
        const at = columnOf(cell.getAttribute('r') ?? '')
        cells[at >= 0 ? at : cells.length] = value.trim()
      }
      return Array.from(cells, (cell) => cell ?? '').join('\t')
    })
    .join('\n')
}
